import React, { useState } from "react";
import { makeGet } from "@culturehq/client";
import { Loader, Pagination, Table, Icon, Button, SearchBar, Success } from "@culturehq/components";

import { useFetch, usePagination } from "app/hooks";
import { isMultiImageStory, t, Trans } from "app/utils";
import { ApprovedForSocial, Cheer, Story, StoryQuestion } from "chq";

import { Time } from "components/common/Time";
import LightboxStories from "components/common/gallery/LightboxStories";
import PageHeader from "components/display/PageHeader";
import LightboxStoryQuickOptions from "components/common/gallery/LightboxStoryQuickOptions";
import useLatestMedia from "app/hooks/useLatestMedia";
import { useProfile } from "app/context";
import StoryPromptTitle from "./StoryPromptTitle";
import { deleteEntity, updateEntity } from "../mutations";
import JobPostingMappingModal from "./job-posting-modal/JobPostingMappingModal";
import StoryFilterBar from "../StoryFilterBar";
import { filtersToQuery, queryToOptions } from "../EmbedStoriesSlider";

const updateStory = updateEntity("stories");
const deleteStory = deleteEntity("stories");

interface MappedContentRowProps {
  onDelete: (storyId: number) => void;
  onStoryUpdate: (updatedStory: Story) => void;
  onRowClicked: (story: Story) => void;
  onUpdateMappings: (value: ApprovedForSocial | Story | Record<string, string[]>) => void;
  story: Story;

}

const MappedContentRow: React.FC<MappedContentRowProps> = ({
  onRowClicked,
  onUpdateMappings,
  story,
  onDelete,
  onStoryUpdate
}) => {
  const oldestMappedaDate = story.externalMapperValueAssociations
    ?.filter(item => item.mappedDate !== undefined)
    ?.reduce((oldestItem, currentItem) => {
      const oldestDate = new Date(oldestItem.mappedDate);
      const currentDate = new Date(currentItem.mappedDate);
      return currentDate < oldestDate ? currentItem : oldestItem;
    });

  const { profile } = useProfile();

  const {
    isFetching: mediaIsFetching,
    thirdPartyMedias,
    onThirdPartyMediasReload,
    onUpdateDraftVersion,
    lastMediaVersion
  } = useLatestMedia(story, profile, { landingPage: undefined });
  const isLastMediaVersionProcessing = lastMediaVersion ? (lastMediaVersion as any).status === "processing" : false;

  return (
    <tr>
      <td className="StoryTable-clickable" onClick={() => onRowClicked(story)}>
        <div className="StoryTable-Details__Thumbnail-Container">
          <img
            className="StoryTable-Details__Image"
            src={story.media.thumbnail || story.media.url}
            alt={story.media.alt || story.question.question}
          />
          {story.media.mediaType === "video" && (
            <Icon className="StoryTable-Details__PlayIcon" icon="play-no-border" />
          )}
          {isMultiImageStory(story) && (
            <Icon className="StoryTable-Details__ImagesIcon" icon="images" />
          )}
        </div>
      </td>
      <StoryPromptTitle
        onReviewClicked={(entity: Story | ApprovedForSocial) => onRowClicked(entity as Story)}
        story={story}
      />
      <td className="StoryTable-clickable" onClick={() => onRowClicked(story)}>
        <p>
          {oldestMappedaDate?.mappedDate ? (
            <Time
              time={oldestMappedaDate?.mappedDate || ""}
              format="%b %e %Y, %l:%M %p"
              keepYear
            />
          ) : (
            <Trans>Not mapped yet</Trans>
          )}
        </p>
      </td>
      <td className="StoryTable-clickable">
        {story.externalMapperValueAssociations?.map(externalMapperValueAssociation => (
          <div key={externalMapperValueAssociation.id} className="chq-bdg">
            {externalMapperValueAssociation.externalMapper.icon && (
              <>
                <span>{externalMapperValueAssociation.externalMapper.icon}</span>
                {" "}
              </>
            )}
            <span>{externalMapperValueAssociation.externalMapperValue.value}</span>
          </div>
        ))}
      </td>
      <td className="Actions-column">
        <LightboxStoryQuickOptions
          className="Admin-QuickOptions"
          isLastMediaVersionProcessing={isLastMediaVersionProcessing}
          lastMediaVersion={lastMediaVersion}
          story={story}
          thirdPartyMediaIsFetching={mediaIsFetching}
          thirdPartyMedias={thirdPartyMedias}
          onDelete={onDelete}
          onUpdate={onStoryUpdate}
          onAnotherModalOpen={() => {}}
          onPauseVideo={() => {}}
          onThirdPartyMediasReload={onThirdPartyMediasReload}
          onUpdateDraftVersion={onUpdateDraftVersion}
          handleApproveClicked={() => {}}
          handleMoveToNextStory={() => {}}
        />
        <div className="admin-story-action-buttons">
          <JobPostingMappingModal
            editImmediately
            story={story}
            onUpdate={onUpdateMappings}
          >
            {(onOpen: () => void) => (
              <Button onClick={onOpen}>
                <Icon icon="edit" /> Edit
              </Button>
            )}
          </JobPostingMappingModal>
        </div>
      </td>
    </tr>
  );
};

interface StoriesJobMappingProps {
  onUpdate: (value: ApprovedForSocial | Story | StoryQuestion) => void;
  match: {
    params: {
      storyId?: string;
    };
  };
}

const StoriesJobMapping: React.FC<StoriesJobMappingProps> = () => {
  const [page, onPageClick] = usePagination();
  const [activeStory, setActiveStory] = useState<Story>();
  const [openModal, setOpenModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const pageToUse = typeof page === "number" ? page : 1;

  const {
    fetching,
    json: { stories = [], pagination = {} },
    onUpdate: onStoriesUpdate
  } = useFetch(() => {
    const query = filtersToQuery(selectedFilters);
    const params: any = { ...queryToOptions(query), page: pageToUse || 1 };
    const [bodySearch] = selectedFilters;
    if (typeof bodySearch === "string") params.bodySearch = bodySearch;
    return makeGet("/stories/mapped", params);
  }, [page, selectedFilters]);

  const onUpdate = (story: Story) => onStoriesUpdate(updateStory(story));
  const onDelete = (story: Story) => onStoriesUpdate(deleteStory(story));

  const handleRowClicked = (story: Story) => {
    setActiveStory(story);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleStoryChange = (goToIndex: number) => {
    let target = goToIndex;

    if (stories.length === 1 && goToIndex === 1) {
      setOpenModal(false);
      setActiveStory(undefined);
      return;
    }

    if (goToIndex === -1) {
      target = stories.length - 1;
    }

    if (goToIndex > stories.length - 1) {
      target = 0;
    }

    setActiveStory(stories[target]);
    setOpenModal(true);
  };

  const onStoryUpdate = (updatedStory: Story) => {
    onUpdate(updatedStory);
    setActiveStory(updatedStory);
  };

  const handleCommentsChange = (story: Story, numberOfComments: number) => {
    const storyToUpdate = stories.find((oldStory: Story) => oldStory.id === story.id);
    onUpdate({ ...storyToUpdate, commentCount: numberOfComments });
  };

  const handleStoryRemove = (storyId: number) => {
    const storyToRemove = stories.find((oldStory: Story) => oldStory.id === storyId);
    onDelete(storyToRemove);
    setOpenModal(false);
    setActiveStory(undefined);
  };

  const handleStoryCheerToggle = (storyId: number, cheerForUser: Cheer, cheersCount: number) => {
    const storyToUpdate = stories.find((oldStory: Story) => oldStory.id === storyId);
    onUpdate({ ...storyToUpdate, cheerForUser, cheersCount });
  };

  const handleUpdateMappings = (value: ApprovedForSocial | Story | Record<string, string[]>) => {
    const updatedStory = value as Story;
    if ((updatedStory.externalMapperValueAssociations?.length || 0) > 0) {
      onUpdate(updatedStory);
    } else {
      onDelete(updatedStory);
    }
  };

  const handleSelectedTags = (updatedOptions: any, _: any) => {
    setSelectedFilters(updatedOptions);
  };

  return (
    <div className="padding-left-15px padding-right-15px StoryApprovals">
      <LightboxStories
        className="gallery-lightbox--Story-Review"
        modalIsOpen={openModal}
        modalStyle={{ overlay: { overflow: "hidden" } }}
        activeStory={activeStory}
        hideShareLink={false}
        currentUserAnswered
        stories={stories}
        onClose={handleClose}
        onCommentsChange={handleCommentsChange}
        onDelete={handleStoryRemove}
        onStoryCheerToggle={handleStoryCheerToggle}
        onStoryUpdate={onStoryUpdate}
        onStoryChange={handleStoryChange}
        mappedVersion
        onKeepAsInternalClicked={() => {}}
        onApproveClicked={() => {}}
        onUpdateMappings={handleUpdateMappings}
      />
      <PageHeader title={t("Stories that are mapped to job postings")}>
        <Trans>
          Below is the list of stories that have been mapped to different jobs on your
          organization&apos;s career site. You can have one story or multiple stories
          mapped and can remove them at any time.
        </Trans>
      </PageHeader>
      <StoryFilterBar
        creatable
        selectedOptions={selectedFilters}
        handleSelectedTags={handleSelectedTags}
        icon="ios-search-strong"
      />
      <Loader loading={fetching}>
        <Table className="StoryTable StoryTable__Approved">
          <thead>
            <tr>
              <th className="thumb">
                <Trans>Story</Trans>
              </th>
              <th />
              <th className="users">
                <Trans>Mapped Date</Trans>
              </th>
              <th className="shares-number">
                <Trans>Job IDs</Trans>
              </th>
              <th className="actions">
                <Trans>Actions</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {stories.map((story: Story) => (
              <MappedContentRow
                key={story.id}
                onRowClicked={handleRowClicked}
                onUpdateMappings={handleUpdateMappings}
                story={story}
                onDelete={handleStoryRemove}
                onStoryUpdate={onStoryUpdate}
              />
            ))}
          </tbody>
        </Table>
        {stories.length < 1 && (
          <Success>
            <Trans>
              No mapped approved stories yet.
            </Trans>
          </Success>
        )}
        <Pagination {...pagination} onClick={onPageClick} />
      </Loader>
    </div>
  );
};

export default StoriesJobMapping;
